import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  fetchAccountGroupsSilent,
  setAccountGroupEditing,
  useAdminSettingsAccountGroups,
  useAdminSettingsEditAccountGroupResult as useAdminSettingsUpdateResult,
} from '../adminSettingsSlice';
import { ContentsHeader } from 'components/ContentsHeader';
import msgId from 'resources/intl';
import { AddCircle as AddCircleIcon, Search as SearchIcon } from '@mui/icons-material';
import colors from 'resources/colors';
import { NormalButton } from 'components/NormalButton';
import { DataGrid, Column, FilterRow, Scrolling, Selection } from 'devextreme-react/data-grid';
import dimens from 'resources/dimens';
import { DxDataGridColumn } from 'components/DxDataGrid';
import constants from 'resources/constants';
import { AccountGroup } from 'models/accounts';
import { AccountGroupEditDialog } from './AccountGroupEditDialog';
import { EditorPreparingEvent } from 'devextreme/ui/data_grid';
import config from 'resources/config';
import dayjs from 'dayjs';
import { getTodayLimit } from 'utils/misc';

interface AccountGroupManagementProps {
  open: boolean;
  height: number;
}

export function AccountGroupManagement(props: AccountGroupManagementProps): JSX.Element {
  const { open, height } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const accountGroups = useAdminSettingsAccountGroups();
  const updateResult = useAdminSettingsUpdateResult();
  const dataGridRef = useRef<DataGrid<AccountGroup, unknown>>(null);
  const minDate = dayjs(config.minDate).toDate();
  const maxDate = dayjs(getTodayLimit()).toDate();
  const columns: DxDataGridColumn[] = [
    {
      key: 'accountGroupId',
      dataField: 'accountGroupId',
      caption: intl.formatMessage({ id: msgId.accountGroupId }),
      width: 100,
      defaultSortOrder: 'asc',
    },
    {
      key: 'accountGroupName',
      dataField: 'accountGroupName',
      caption: intl.formatMessage({ id: msgId.accountGroupName }),
    },
    {
      key: 'accountGroupUrlText',
      dataField: 'accountGroupUrlText',
      caption: intl.formatMessage({ id: msgId.accountGroupUrl }),
      width: 300,
    },
    {
      key: 'createdAt',
      dataField: 'createdAt',
      dataType: 'datetime',
      filterOperations: constants.dataGrid.dateFilterOperations,
      format: constants.dateFormat.yyyyMMddHHmm,
      caption: intl.formatMessage({ id: msgId.createdAt }),
      width: 160,
      editorOptions: { min: minDate, max: maxDate },
    },
    {
      key: 'updatedAt',
      dataField: 'updatedAt',
      dataType: 'datetime',
      filterOperations: constants.dataGrid.dateFilterOperations,
      format: constants.dateFormat.yyyyMMddHHmm,
      caption: intl.formatMessage({ id: msgId.updatedAt }),
      width: 160,
      editorOptions: { min: minDate, max: maxDate },
    },
  ];

  /**
   * 行選択
   * @param accountGroup アカウントグループ
   */
  const handleSelectionChanged = (accountGroup: AccountGroup) => {
    dataGridRef.current?.instance.clearSelection();
    if (accountGroup != null) {
      dispatch(setAccountGroupEditing({ editing: true, accountGroup: accountGroup }));
    }
  };

  /**
   * 新規登録
   */
  const handleRegistrationClick = () => {
    dispatch(setAccountGroupEditing({ editing: true, accountGroup: undefined }));
  };

  /**
   * フィルターリセット
   */
  const handleFilterResetClick = () => {
    if (dataGridRef.current) {
      const dataGrid = dataGridRef.current.instance;
      columns.forEach((c) => {
        dataGrid.columnOption(c.dataField, 'selectedFilterOperation', null);
      });
      dataGrid.clearFilter();
    }
  };

  // 日付入力のフィルターをreadonlyにする。
  const setInputReadOnly = () => {
    const element = document.getElementById('accountGroupDataGrid');
    if (element) {
      const elements = [4, 5];
      elements.forEach((colIndex) => {
        const dateFilterInput = element.querySelector(
          '.dx-datagrid-filter-row td[aria-colindex="' + colIndex + '"]'
        ) as HTMLElement;
        if (dateFilterInput) {
          const inputElement = dateFilterInput.querySelector(
            '.dx-texteditor-input'
          ) as HTMLInputElement;
          if (inputElement) {
            inputElement.readOnly = true;
          }
        }
      });
    }
  };

  /**
   * API完了後Account Group情報を再取得
   */
  useEffect(() => {
    if (open && updateResult && updateResult.isSuccess) {
      dispatch(fetchAccountGroupsSilent());
    }
  }, [dispatch, open, updateResult]);

  return (
    <Box
      sx={{
        position: 'absolute',
        width: '100%',
        background: colors.subDrawer.background,
        visibility: open ? 'visible' : 'hidden',
      }}
    >
      {accountGroups && (
        <React.Fragment>
          <ContentsHeader title={intl.formatMessage({ id: msgId.groupManagement })}>
            <NormalButton
              label={intl.formatMessage({ id: msgId.filterReset })}
              color="primary"
              startIcon={<SearchIcon />}
              onClick={() => handleFilterResetClick()}
            />
            <NormalButton
              label={intl.formatMessage({ id: msgId.newRegistration })}
              color="primary"
              startIcon={<AddCircleIcon />}
              onClick={() => handleRegistrationClick()}
            />
          </ContentsHeader>
          <DataGrid
            id="accountGroupDataGrid"
            style={{ margin: dimens.dataGrid.margin }}
            ref={dataGridRef}
            height={height - dimens.dataGrid.offsetHeight}
            dataSource={accountGroups}
            showBorders={true}
            allowColumnResizing={true}
            hoverStateEnabled={true}
            columnResizingMode="nextColumn"
            keyExpr="accountGroupId"
            columnAutoWidth={true}
            noDataText={intl.formatMessage({ id: msgId.noResults })}
            onSelectionChanged={(e) => handleSelectionChanged(e.selectedRowsData[0])}
            onCellClick={setInputReadOnly}
          >
            <Selection mode="single" />
            <FilterRow visible={true} />
            <Scrolling mode="virtual" />
            {columns.map((column) => {
              return (
                <Column
                  key={column.key}
                  name={column.key}
                  dataField={column.dataField}
                  minWidth={dimens.dataGrid.column.minWidth}
                  dataType={column.dataType}
                  format={column.format}
                  caption={column.caption}
                  width={column.width}
                  filterOperations={column.filterOperations}
                  defaultSortOrder={column.defaultSortOrder}
                  editorOptions={column.editorOptions}
                />
              );
            })}
          </DataGrid>
        </React.Fragment>
      )}
      <AccountGroupEditDialog contentHeight={height} />
    </Box>
  );
}
